import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import PincodeModal from "@/components/PincodeModal";
import AddressSelector from "@/components/AddressSelector";
import GuestLoginPrompt from "@/components/GuestLoginPrompt";
import { MapPin, MagnifyingGlass, Lightning, Plus, Minus, ShoppingCart, User, CaretDown } from "@phosphor-icons/react";

export default function Storefront() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, hasRole, switchRole } = useAuth();
  const { add, decrement, getStoreCart, itemCount, subtotal, pincode, setPincode, selectedAddress, setSelectedAddress } = useCart();
  const [store, setStore] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [query, setQuery] = useState("");
  const [pincodeInfo, setPincodeInfo] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [showAddrSelector, setShowAddrSelector] = useState(false);

  // Load store
  useEffect(() => {
    api.get(`/storefront/${slug}`).then((r) => setStore(r.data)).catch(() => setStore(false));
    api.get(`/storefront/${slug}/categories`).then((r) => setCategories(r.data));
  }, [slug]);

  // Logged-in users: auto-prefer their saved default address (do NOT force pincode modal)
  useEffect(() => {
    if (!user || pincode) return;
    api.get("/customer/addresses").then((r) => {
      const def = (r.data || []).find((a) => a.is_default) || (r.data || [])[0];
      if (def) setSelectedAddress(def);
    }).catch(() => {});
  }, [user, pincode, setSelectedAddress]);

  // Guests: NEVER auto-open pincode modal — allow open browsing (Blinkit-style).
  // Pincode is only required at checkout, prompted in CartPage.

  // Pincode serviceability for this store (only when pincode is set)
  useEffect(() => {
    if (!slug || !pincode || pincode.length !== 6) { setPincodeInfo(null); return; }
    api.get(`/storefront/${slug}/pincode/${pincode}`).then((r) => setPincodeInfo(r.data));
  }, [slug, pincode]);

  // Product fetching
  useEffect(() => {
    api.get(`/storefront/${slug}/products`, { params: { ...(activeCat && { category_id: activeCat }), ...(query && { q: query }) } })
      .then((r) => setProducts(r.data));
  }, [activeCat, query, slug]);

  if (store === null) return <div className="p-8">Loading…</div>;
  if (store === false) return <div className="p-8 text-center">Store not found</div>;

  const cart = getStoreCart(slug);
  const cartItems = cart.items;
  const serviceable = !!pincodeInfo?.serviceable;
  const browseOnly = pincodeInfo && !pincodeInfo.serviceable;
  const availability = store.availability_status || { is_open: true };
  const storeClosed = !availability.is_open;
  // Guest-friendly: always allow ADD to cart. Cart/checkout will gate on serviceability + closed + auth.
  const handleAddToCart = (p) => {
    const inv = Number(p.inventory ?? 0);
    if (inv <= 0) {
      toast.error(`'${p.name}' is out of stock`);
      return;
    }
    const inCart = cart.items.find((i) => i.product_id === p.id);
    const next = (inCart?.qty || 0) + 1;
    if (next > inv) {
      toast.error(`Only ${inv} quantity available`);
      return;
    }
    add(slug, store.id, p);
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9] pb-32">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-gray-100">
        <div className="px-4 py-3 max-w-5xl mx-auto">
          <div className="flex items-center justify-between gap-3">
            <Link to={`/${slug}`} className="flex items-center gap-2 min-w-0">
              {store.logo
                ? <img src={store.logo} className="w-10 h-10 rounded-xl object-cover" alt="" />
                : <div className="w-10 h-10 rounded-xl bg-[#F8CB46] flex items-center justify-center"><Lightning weight="fill" size={20} /></div>}
              <div className="min-w-0">
                <div className="font-heading font-black text-base truncate" data-testid="store-name">{store.name}</div>
                <div className="text-[10px] text-gray-500">{store.description}</div>
              </div>
            </Link>
            {user ? (
              <div className="relative group" data-testid="storefront-account-menu">
                <button className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-700" data-testid="customer-account-link">
                  <User size={18} />
                </button>
                <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible focus-within:opacity-100 focus-within:visible transition z-50">
                  <Link to="/my-orders" className="block px-4 py-2.5 text-sm font-semibold hover:bg-gray-50 rounded-t-2xl" data-testid="storefront-menu-orders">My orders</Link>
                  <Link to="/account/addresses" className="block px-4 py-2.5 text-sm font-semibold hover:bg-gray-50" data-testid="storefront-menu-addresses">My addresses</Link>
                  {hasRole("shopkeeper") && (
                    <button onClick={async () => { try { await switchRole("shopkeeper"); nav(user?.store_id ? "/admin" : "/onboard"); } catch { toast.error("Failed to switch"); } }}
                      className="w-full text-left px-4 py-2.5 text-sm font-semibold text-[#15803D] hover:bg-gray-50 border-t border-gray-100 rounded-b-2xl"
                      data-testid="storefront-menu-seller-dashboard">
                      Go to Seller Dashboard →
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <Link
                to="/login"
                onClick={() => sessionStorage.setItem("qd_return_to", `/${slug}`)}
                className="inline-flex items-center gap-1.5 bg-[#15803D] text-white text-sm font-bold px-4 py-2 rounded-full hover:bg-[#166534] active:scale-95 transition shadow-sm"
                data-testid="customer-account-link">
                <User size={14} weight="bold" /> Login / Signup
              </Link>
            )}
          </div>

          {/* Delivery / Address banner */}
          <button
            onClick={() => user ? setShowAddrSelector(true) : setShowPinModal(true)}
            className="mt-3 w-full flex items-center gap-2 bg-[#F8CB46]/20 hover:bg-[#F8CB46]/30 rounded-2xl px-3 py-2 text-left transition"
            data-testid="storefront-delivery-banner">
            <Lightning weight="fill" size={18} className="text-[#15803D] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              {!pincode && (
                <>
                  <div className="text-xs font-bold">Set delivery location</div>
                  <div className="text-[10px] text-gray-600">Tap to add address or enter pincode</div>
                </>
              )}
              {pincode && serviceable && !storeClosed && (
                <>
                  <div className="text-xs font-bold">
                    Delivery in <span className="text-[#15803D]">{pincodeInfo.eta_minutes} min</span>
                    {selectedAddress && <span className="text-gray-500"> · {selectedAddress.label}</span>}
                  </div>
                  <div className="text-[10px] text-gray-600 truncate">
                    {selectedAddress
                      ? `${selectedAddress.address}, ${selectedAddress.pincode}`
                      : `${pincodeInfo.area} · ${pincode}`}
                  </div>
                </>
              )}
              {pincode && serviceable && storeClosed && (
                <>
                  <div className="text-xs font-bold text-red-700">Store closed · {availability.reason || "Currently offline"}</div>
                  <div className="text-[10px] text-gray-600">Opens at {availability.open_time || "09:00"} · {pincode}</div>
                </>
              )}
              {pincode && !serviceable && (
                <>
                  <div className="text-xs font-bold text-amber-700">Not serviceable at {pincode}</div>
                  <div className="text-[10px] text-gray-600">Browsing only — tap to change address</div>
                </>
              )}
            </div>
            <CaretDown size={14} className="text-gray-500" />
          </button>

          {/* Search */}
          <div className="mt-3 relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="qd-input pl-10 !bg-white"
              placeholder="Search for products..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="store-search"
            />
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-4">
        {/* Hero banner */}
        {store.banner && (
          <div className="rounded-2xl overflow-hidden mb-5 relative h-32 sm:h-48">
            <img src={store.banner} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-3 left-4 text-white">
              <div className="text-xs uppercase tracking-widest font-bold">Today's pick</div>
              <div className="font-heading font-black text-2xl">Fresh from the shelf</div>
            </div>
          </div>
        )}

        {/* Store closed banner (independent of pincode) */}
        {storeClosed && (
          <div className="qd-card p-4 mb-5 border-2 border-red-200 bg-red-50/60" data-testid="store-closed-card">
            <div className="flex items-start gap-3">
              <div className="inline-flex w-10 h-10 rounded-2xl bg-red-100 items-center justify-center flex-shrink-0">
                <Lightning weight="fill" size={20} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-black text-base">Store closed</h3>
                <p className="text-xs text-gray-700 mt-0.5" data-testid="store-closed-reason">
                  {availability.reason || "Currently not accepting orders"}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Business hours: <span className="font-mono font-bold">{availability.open_time || "09:00"} – {availability.close_time || "22:00"}</span> IST
                </p>
                <p className="text-xs text-gray-500 mt-2">You can still browse the catalog. Checkout will reopen once the store is online.</p>
              </div>
            </div>
          </div>
        )}

        {/* Not serviceable banner (browse-only) */}
        {browseOnly && (
          <div className="qd-card p-4 mb-5 border-2 border-amber-200 bg-amber-50/50" data-testid="not-serviceable-card">
            <div className="flex items-start gap-3">
              <div className="inline-flex w-10 h-10 rounded-2xl bg-amber-100 items-center justify-center flex-shrink-0">
                <MapPin weight="fill" size={20} className="text-amber-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-black text-base">Not delivering to {pincode}</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  You can still browse {store.name}'s catalog. Add a serviceable address to order.
                </p>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <button onClick={() => user ? setShowAddrSelector(true) : setShowPinModal(true)}
                    className="qd-btn-primary text-xs" data-testid="not-serv-change-addr">
                    {user ? "Change address" : "Change pincode"}
                  </button>
                  <Link to="/home" className="qd-btn-green text-xs inline-flex" data-testid="not-serv-marketplace">
                    Browse other stores
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Category chips */}
        <div className="mb-4">
          <h2 className="font-heading font-black text-xl mb-2">Shop by category</h2>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            <button onClick={() => setActiveCat(null)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${!activeCat ? "bg-black text-white" : "bg-white border border-gray-200"}`}
              data-testid="cat-chip-all">
              All
            </button>
            {categories.map((c) => (
              <button key={c.id} onClick={() => setActiveCat(c.id === activeCat ? null : c.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1 ${activeCat === c.id ? "bg-black text-white" : "bg-white border border-gray-200"}`}
                data-testid={`cat-chip-${c.name}`}>
                {c.icon && <img src={c.icon} alt="" className="w-4 h-4" />}
                {c.name}
              </button>
            ))}
          </div>
        </div>

        {/* Product grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {products.map((p) => {
            const inCart = cartItems.find((i) => i.product_id === p.id);
            const inv = Number(p.inventory ?? 0);
            const oos = inv <= 0;
            const lowStock = !oos && inv <= 5;
            const atMax = inCart && inCart.qty >= inv;
            return (
              <div key={p.id}
                className={`qd-card overflow-hidden ${oos ? "opacity-70" : ""}`}
                data-testid={`store-product-${p.id}`}>
                <Link to={`/${slug}/product/${p.id}`}>
                  <div className="aspect-square bg-gray-50 overflow-hidden relative">
                    {p.images?.[0] && <img src={p.images[0]} alt={p.name} className={`w-full h-full object-cover ${oos ? "grayscale" : ""}`} />}
                    {oos && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="bg-white text-red-600 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full"
                          data-testid={`oos-badge-${p.id}`}>Out of stock</span>
                      </div>
                    )}
                    {!oos && p.discount_pct > 0 && (
                      <div className="absolute top-2 left-2 bg-[#15803D] text-white text-[10px] font-black px-2 py-0.5 rounded-full">
                        {p.discount_pct}% OFF
                      </div>
                    )}
                    {!oos && lowStock && (
                      <div className="absolute bottom-2 left-2 bg-amber-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full"
                        data-testid={`low-stock-${p.id}`}>
                        Only {inv} left
                      </div>
                    )}
                    {!oos && serviceable && (
                      <div className="absolute top-2 right-2 bg-white/90 rounded-full px-2 py-0.5 text-[10px] font-bold flex items-center gap-1">
                        <Lightning weight="fill" size={10} className="text-[#15803D]" />
                        {pincodeInfo?.eta_minutes || 30} min
                      </div>
                    )}
                  </div>
                </Link>
                <div className="p-2.5">
                  <div className="text-[10px] text-gray-500">{p.weight}</div>
                  <div className="text-sm font-semibold line-clamp-2 min-h-[2.5rem]">{p.name}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <div>
                      <div className="font-heading font-black">₹{p.price}</div>
                      {p.mrp > p.price && <div className="text-[10px] text-gray-400 line-through">₹{p.mrp}</div>}
                    </div>
                    {oos ? (
                      <button disabled
                        className="bg-gray-100 text-gray-400 font-bold text-xs px-3 py-1 rounded-lg cursor-not-allowed"
                        data-testid={`store-oos-${p.id}`}>
                        Sold out
                      </button>
                    ) : inCart ? (
                      <div className="flex items-center gap-1 bg-[#15803D] text-white rounded-lg overflow-hidden">
                        <button onClick={() => decrement(slug, p.id)} className="px-2 py-1.5" data-testid={`store-dec-${p.id}`}><Minus size={12} weight="bold" /></button>
                        <span className="text-xs font-bold min-w-[20px] text-center">{inCart.qty}</span>
                        <button onClick={() => handleAddToCart(p)} disabled={atMax}
                          className={`px-2 py-1.5 ${atMax ? "opacity-50 cursor-not-allowed" : ""}`}
                          data-testid={`store-inc-${p.id}`}><Plus size={12} weight="bold" /></button>
                      </div>
                    ) : (
                      <button onClick={() => handleAddToCart(p)}
                        className="border-2 border-[#15803D] text-[#15803D] font-bold text-xs px-3 py-1 rounded-lg hover:bg-[#15803D] hover:text-white transition active:scale-95"
                        data-testid={`store-add-${p.id}`}>
                        ADD
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {products.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">No products found</div>
          )}
        </div>
      </div>

      {/* Sticky bottom cart - always visible whenever items in cart (open browsing) */}
      {itemCount(slug) > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 p-3 fade-up">
          <button onClick={() => nav(`/${slug}/cart`)}
            className="w-full max-w-3xl mx-auto bg-[#15803D] text-white rounded-2xl p-4 flex items-center justify-between shadow-2xl hover:bg-[#166534] active:scale-[0.98] transition"
            data-testid="store-sticky-cart">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center relative">
                <ShoppingCart weight="fill" size={20} />
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#F8CB46] text-black text-[10px] font-black flex items-center justify-center">{itemCount(slug)}</span>
              </div>
              <div className="text-left">
                <div className="font-heading font-black">{itemCount(slug)} item{itemCount(slug) > 1 && "s"}</div>
                <div className="text-xs text-white/80">₹{subtotal(slug)} · View cart</div>
              </div>
            </div>
            <div className="font-heading font-black">Checkout →</div>
          </button>
        </div>
      )}

      {/* Pincode-only modal (for guests / first visit) */}
      {showPinModal && (
        <PincodeModal
          initial={pincode}
          onClose={() => setShowPinModal(false)}
          onSave={(pin) => {
            setPincode(pin);
            setShowPinModal(false);
          }}
        />
      )}

      {/* Address selector for logged-in users */}
      {showAddrSelector && user && (
        <AddressSelector
          selectedId={selectedAddress?.id}
          onClose={() => setShowAddrSelector(false)}
          onSelect={(addr) => {
            setSelectedAddress(addr);
            setShowAddrSelector(false);
            toast.success(`Delivering to ${addr.label} · ${addr.pincode}`);
          }}
        />
      )}

      {/* Floating guest login prompt — non-blocking */}
      <GuestLoginPrompt returnTo={`/${slug}`} alwaysShow />
    </div>
  );
}
